import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  PropsWithChildren,
  useEffect,
} from 'react';
import { HighlightColor, Annotation } from '../../types';
import { HighlightColorName } from '../../../Highlighting/types';
import { useAnnotationDialogsContext } from '../../AnnotationDialogsContext';
import { useSessionContext } from '../../../../components/SessionContext';

export interface FormData {
  note: string | null;
  highlightColor: HighlightColor;
}

function getInitialFormData(annotation: Annotation | null): FormData {
  return {
    note: annotation?.note ?? '',
    highlightColor: annotation?.display.color ?? HighlightColorName.none,
  };
}

export interface AnnotationFormContextValue {
  formData: FormData;
  updateFormData: (fields: Partial<FormData>) => void;
  saveAnnotation: () => Promise<void>;
}

export const AnnotationFormContext = createContext<
  AnnotationFormContextValue | undefined
>(undefined);

export default function AnnotationFormProvider({
  children,
}: Readonly<PropsWithChildren>) {
  // Context
  const { selectedAnnotation: annotation } = useAnnotationDialogsContext();
  const { updateAnnotation } = useSessionContext();

  // Form state
  const [formData, setFormData] = useState<FormData>(
    getInitialFormData(annotation),
  );

  // Update form data when selected annotation changes
  useEffect(() => {
    setFormData(getInitialFormData(annotation));
  }, [annotation]);

  // Actions
  const updateFormData = useCallback((fields: Partial<FormData>) => {
    setFormData((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        ...fields,
      };
    });
  }, []);

  const saveAnnotation = useCallback(async () => {
    const updatedAnnotation = structuredClone(annotation as Annotation);

    updatedAnnotation.note = formData.note;
    updatedAnnotation.display.color = formData.highlightColor;

    updateAnnotation(updatedAnnotation);
  }, [annotation, formData, updateAnnotation]);

  // Context value
  const contextValue = useMemo(
    () => ({
      formData,
      updateFormData,
      saveAnnotation,
    }),
    [formData, updateFormData, saveAnnotation],
  );

  return (
    <AnnotationFormContext.Provider value={contextValue}>
      {children}
    </AnnotationFormContext.Provider>
  );
}

export function useAnnotationFormContext(): AnnotationFormContextValue {
  const ctx = useContext(AnnotationFormContext);
  if (!ctx) {
    throw new Error(
      'useAnnotationFormContext must be used within an AnnotationFormProvider',
    );
  }
  return ctx;
}
